import React from "react";

export type OfficerSession = {
  id: string;
  displayName: string | null;
};

export type OfficerProfile = {
  staffNumber?: string | null;
  userId?: string | null;
  fullName?: string | null;
  email?: string | null;
  mobileNumber?: string | null;
  assignment?: string | null;
  portOfEntry?: string | null;
  province?: string | null;
  roleCode?: string | null;
  accountStatus?: string | null;
  userRole?: string | null;
};

type UnknownRecord = Record<string, unknown>;

type AuthSession = {
  officerId: string | null;
  displayName: string | null;
  claims: UnknownRecord | null;
  profile: OfficerProfile | null;
};

export const AUTH_TOKEN_STORAGE_KEY = "auth_token";

const PROFILE_STORAGE_KEYS = [
  "auth_profile",
  "auth_user",
  "user_profile",
  "current_user",
  "current_user_profile",
  "phmis_user",
  "phmis_profile",
];

const KNOWN_SEGMENT_KEYS = [
  "user",
  "officer",
  "profile",
  "account",
  "details",
  "identity",
  "principal",
  "data",
  "claims",
  "session",
  "context",
];

const ID_FIELDS = [
  "userId",
  "user_id",
  "accountId",
  "account_id",
  "sub",
  "id",
  "officerId",
  "officer_id",
  "staffNumber",
  "staff_number",
];

const FULL_NAME_FIELDS = ["fullName", "full_name", "displayName", "display_name", "officerName", "officer_name", "name"];
const GIVEN_NAME_FIELDS = ["givenName", "given_name", "firstName"];
const FAMILY_NAME_FIELDS = ["familyName", "family_name", "lastName", "surname"];
const USERNAME_FIELDS = ["preferred_username", "username", "userName", "email"];

const PROFILE_FIELD_MAP: Record<keyof OfficerProfile, string[]> = {
  staffNumber: ["staffNumber", "staff_number", "staffNo", "staff_no"],
  userId: ["userId", "user_id", "sub", "id"],
  fullName: FULL_NAME_FIELDS,
  email: ["email", "emailAddress", "email_address"],
  mobileNumber: ["mobileNumber", "mobile_number", "phoneNumber", "phone_number", "phone", "contactNumber", "contact_number"],

  assignment: ["assignment"],
  portOfEntry: ["portOfEntry", "port_of_entry", "dutyStation", "duty_station"],
  province: ["province", "provinceName", "province_name", "region"],
  roleCode: ["roleCode", "role_code"],
  accountStatus: ["accountStatus", "account_status", "status"],
  userRole: ["userRole", "user_role", "role"],

};

function getPotentialStorages(): Storage[] {
  const stores: Storage[] = [];
  const globalTarget: any = typeof window !== "undefined" ? window : typeof globalThis !== "undefined" ? globalThis : null;
  if (!globalTarget) return stores;

  for (const key of ["localStorage", "sessionStorage"]) {
    try {
      const store = globalTarget[key] as Storage | undefined;
      if (store) stores.push(store);
    } catch {
      // Accessing storage can throw (e.g., disabled cookies). Ignore and continue.
    }
  }

  return stores;
}

function readFromStorages(key: string): string | null {
  for (const storage of getPotentialStorages()) {
    try {
      const value = storage.getItem(key);
      if (value) return value;
    } catch {
      // Ignore storage access failures.
    }
  }
  return null;
}

function safeJsonParse(value: string | null): UnknownRecord | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return typeof parsed === "object" && parsed ? (parsed as UnknownRecord) : null;
  } catch {
    return null;
  }
}

type StringOptions = {
  allowNumber?: boolean;
  rejectNumeric?: boolean;
};

function toTrimmedString(value: unknown, options: StringOptions = {}): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (options.rejectNumeric && /^\d+(?:\.\d+)?$/.test(trimmed)) return null;
    return trimmed;
  }
  if (typeof value === "number" && Number.isFinite(value) && options.allowNumber) {
    return String(value);
 
  }
  return null;
}

function collectSegments(value: unknown): UnknownRecord[] {
  const segments: UnknownRecord[] = [];
  const queue: UnknownRecord[] = [];
  const seen = new Set<unknown>();

   const enqueue = (candidate: unknown) => {
    if (!candidate || typeof candidate !== "object") return;
    if (seen.has(candidate)) return;
    seen.add(candidate);
    queue.push(candidate as UnknownRecord);
  };

  enqueue(value);

  while (queue.length) {
    const current = queue.shift()!;
    segments.push(current);
 

    for (const key of KNOWN_SEGMENT_KEYS) {
      const nested = current[key];
      if (Array.isArray(nested)) {
        nested.forEach(item => enqueue(item));
      } else {
        enqueue(nested);
      }
    }
  }

  return segments;
}

function pickField(segments: UnknownRecord[], fields: string[], options: StringOptions = {}): string | null {
  for (const field of fields) {
    for (const segment of segments) {
      const candidate = toTrimmedString(segment[field], options);
      if (candidate) return candidate;
    }
  }
  return null;
}

function buildOfficerProfile(segments: UnknownRecord[]): OfficerProfile | null {
  const profile: OfficerProfile = {};

  profile.staffNumber = pickField(segments, PROFILE_FIELD_MAP.staffNumber, { allowNumber: true }) ?? null;
  profile.userId = pickField(segments, PROFILE_FIELD_MAP.userId, { allowNumber: true }) ?? null;
  profile.fullName = pickField(segments, PROFILE_FIELD_MAP.fullName, { rejectNumeric: true }) ?? null;
  profile.email = pickField(segments, PROFILE_FIELD_MAP.email) ?? null;
  profile.mobileNumber = pickField(segments, PROFILE_FIELD_MAP.mobileNumber) ?? null;
  profile.assignment = pickField(segments, PROFILE_FIELD_MAP.assignment) ?? null;
  profile.portOfEntry = pickField(segments, PROFILE_FIELD_MAP.portOfEntry) ?? null;
  profile.province = pickField(segments, PROFILE_FIELD_MAP.province) ?? null;
  profile.roleCode = pickField(segments, PROFILE_FIELD_MAP.roleCode) ?? null;
  profile.accountStatus = pickField(segments, PROFILE_FIELD_MAP.accountStatus) ?? null;
  profile.userRole = pickField(segments, PROFILE_FIELD_MAP.userRole) ?? null;

  if (!profile.fullName) {
    const given = pickField(segments, GIVEN_NAME_FIELDS, { rejectNumeric: true });
    const family = pickField(segments, FAMILY_NAME_FIELDS, { rejectNumeric: true });
    const combined = [given, family].filter(Boolean).join(" ").trim();
    if (combined) profile.fullName = combined;
  }

  return Object.values(profile).some(value => value != null && value !== "") ? profile : null;
}

function buildOfficerSession(segments: UnknownRecord[]): OfficerSession | null {
  let id = pickField(segments, ID_FIELDS, { allowNumber: true });

  const fullName = pickField(segments, FULL_NAME_FIELDS, { rejectNumeric: true });
  const given = pickField(segments, GIVEN_NAME_FIELDS, { rejectNumeric: true });
  const family = pickField(segments, FAMILY_NAME_FIELDS, { rejectNumeric: true });

  let display = fullName;
  if (!display && (given || family)) {
    display = [given, family].filter(Boolean).join(" ").trim() || null;
  }
  if (!display) {
    display = pickField(segments, USERNAME_FIELDS, { rejectNumeric: true }) ?? null;
  }

  if (!display && id && !/^\d+(?:\.\d+)?$/.test(id)) {
    display = id;
  }

  if (!id && display) {
    id = display;
  }

  if (!id && !display) {
    return null;
  }

  return {
    id: id ?? "",
    displayName: display ?? null,
  };
}

function decodeBase64Url(segment: string): string | null {
  if (!segment) return null;
  const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padding = normalized.length % 4 ? 4 - (normalized.length % 4) : 0;
  const padded = normalized + "=".repeat(padding);

  try {
    if (typeof Buffer !== "undefined") {
      return Buffer.from(padded, "base64").toString("utf-8");
    }
  } catch {
    // Buffer may be unavailable or throw in some environments.
  }

  const globalAtob: ((input: string) => string) | undefined =
    typeof atob === "function"
      ? atob
      : typeof window !== "undefined" && typeof window.atob === "function"
        ? window.atob.bind(window)
        : undefined;

  if (!globalAtob) return null;

  try {
    return globalAtob(padded);
  } catch {
    return null;
  }
}

function parseTokenPayload(token: string | null): UnknownRecord | null {
  if (!token) return null;
  const trimmed = token.trim();
  if (!trimmed) return null;
  const withoutBearer = trimmed.replace(/^Bearer\s+/i, "");
  const parts = withoutBearer.split(".");
  if (parts.length < 2) return null;
  const payload = decodeBase64Url(parts[1]);
  if (!payload) return null;
  try {
    const parsed = JSON.parse(payload);
    return typeof parsed === "object" && parsed ? (parsed as UnknownRecord) : null;
  } catch {
    return null;
  }
}

function buildOfficerFromClaims(claims: UnknownRecord | null): OfficerSession | null {
  if (!claims) return null;
  const segments = collectSegments(claims);
  return buildOfficerSession(segments);
}

function buildProfileFromClaims(claims: UnknownRecord | null): OfficerProfile | null {
  if (!claims) return null;
  const segments = collectSegments(claims);
  return buildOfficerProfile(segments);
}

function getOfficerFromProfiles(): OfficerSession | null {
  for (const key of PROFILE_STORAGE_KEYS) {
    const raw = readFromStorages(key);
    if (!raw) continue;
    const parsed = safeJsonParse(raw);
    const officer = buildOfficerFromClaims(parsed);
    if (officer) return officer;
  }
  return null;
}

function getProfileFromProfiles(): OfficerProfile | null {
  for (const key of PROFILE_STORAGE_KEYS) {
    const raw = readFromStorages(key);
    if (!raw) continue;
    const parsed = safeJsonParse(raw);
    const profile = buildProfileFromClaims(parsed);
    if (profile) return profile;
  }

  return null;
}

export function getCurrentOfficer(): OfficerSession | null {
  const storedOfficer = getOfficerFromProfiles();
  if (storedOfficer) return storedOfficer;

  const token = readFromStorages(AUTH_TOKEN_STORAGE_KEY);
  const claims = parseTokenPayload(token);
  if (!claims) return null;

  const segments = collectSegments(claims);
  const officer = buildOfficerSession(segments);
  if (officer) return officer;

  const profile = buildOfficerProfile(segments);
  if (!profile) return null;

  const fallbackId = profile.staffNumber ?? profile.userId ?? profile.fullName ?? null;
  const fallbackName = profile.fullName ?? profile.staffNumber ?? profile.userId ?? null;
  if (!fallbackId && !fallbackName) return null;

  return {
    id: fallbackId ?? fallbackName ?? "",
    displayName: fallbackName ?? null,
  };

}

export function clearAuthSession() {
  for (const storage of getPotentialStorages()) {
    try {
      storage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    } catch {
      // Ignore storage access failures when clearing the session.
    }

    for (const key of PROFILE_STORAGE_KEYS) {
      try {
        storage.removeItem(key);
      } catch {
        // Continue clearing remaining keys even if one removal fails.
      }
    }
  }
}

export function getCurrentOfficerId(): string | null {
  return getCurrentOfficer()?.id ?? null;
}

export function getCurrentOfficerDisplayName(): string | null {
  return getCurrentOfficer()?.displayName ?? null;
}

export function getAuthSession(): AuthSession {
  const token = readFromStorages(AUTH_TOKEN_STORAGE_KEY);
  const claims = parseTokenPayload(token);
  const segments = claims ? collectSegments(claims) : null;

  const officerFromClaims = segments ? buildOfficerSession(segments) : null;
  const profileFromClaims = segments ? buildOfficerProfile(segments) : null;

  const officer = officerFromClaims ?? getOfficerFromProfiles();
  const profile = profileFromClaims ?? getProfileFromProfiles();
  const fallbackSegments = segments ?? [];

  const officerId =
    officer?.id ??
    profile?.staffNumber ??
    profile?.userId ??
    pickField(fallbackSegments, ID_FIELDS, { allowNumber: true }) ??
    null;

  const displayName = officer?.displayName ?? profile?.fullName ?? null;

  return {
    officerId,
    displayName,
    claims: claims ?? null,
    profile: profile ?? null,

  };
}

function subscribeToSessionUpdates(callback: () => void) {
  if (typeof window === "undefined") {
    return () => {};
  }
  const handler = () => callback();
  window.addEventListener("storage", handler);
  window.addEventListener("phmis:session", handler as EventListener);
  return () => {
    window.removeEventListener("storage", handler);
    window.removeEventListener("phmis:session", handler as EventListener);
  };
}

export function useAuthSession(): AuthSession {
  const [session, setSession] = React.useState<AuthSession>(() => getAuthSession());

  React.useEffect(() => subscribeToSessionUpdates(() => setSession(getAuthSession())), []);

  return session;
}

export function decodeTokenClaims(token: string | null): UnknownRecord | null {
  return parseTokenPayload(token);
}

export const __testables__ = {
  decodeBase64Url,
  parseTokenPayload,
  buildOfficerFromClaims,
  buildProfileFromClaims,
  collectSegments,
  pickField,

};
